import { catchSentryError } from "@utils/sentry";
import { useEffect, useState, useCallback } from "react";

// Custom hook for managing state in localStorage
export default function useLocalStorage<ValueType>(
  key: string,
  defaultValue: ValueType
) {
  // Initialize state with value from localStorage or default value
  const [value, setValue] = useState(() => {
    try {
      const storedValue = localStorage.getItem(key);
      return storedValue ? JSON.parse(storedValue) : defaultValue;
    } catch (error) {
      catchSentryError(error, true, {
        level: "warning",
        tags: { operation: "local_storage_read" }
      });
      return defaultValue;
    }
  });

  // Update state when localStorage changes
  useEffect(() => {
    const listener = (e: StorageEvent) => {
      if (e.storageArea === localStorage && e.key === key) {
        setValue(e.newValue ? JSON.parse(e.newValue) : e.newValue);
      }
    };

    window.addEventListener("storage", listener);

    return () => {
      window.removeEventListener("storage", listener);
    };
  }, [key]);

  // Update value in localStorage and state
  const setValueInLocalStorage = useCallback(
    (newValue: ValueType) => {
      setValue((currentValue: ValueType) => {
        const updatedValue =
          typeof newValue === "function" ? newValue(currentValue) : newValue;
        try {
          localStorage.setItem(key, JSON.stringify(updatedValue));
          return updatedValue;
        } catch (error) {
          catchSentryError(error, true, {
            level: "warning",
            tags: { operation: "local_storage_write" }
          });
          return currentValue;
        }
      });
    },
    [key]
  );

  return [value, setValueInLocalStorage];
}
